/**
 * Past papers as the subject page lists them (bug fix 1, spec 4 §4): one row per paper under
 * its session, its files as chips by role. Pure. The references and sessions shown are the
 * student's own documents; nothing here names a board (rule 8).
 */
import type { Doc, PastPaper, PastPaperListing, PastPaperRole } from "../api/api";

export const ROLE_WORD: Record<PastPaperRole, string> = {
  question_paper: "Question paper",
  mark_scheme: "Mark scheme",
  insert: "Insert",
};

const SEASON_RANK: Record<string, number> = { winter: 0, summer: 1, autumn: 2 };

/** "summer 2025" → "Summer 2025"; no series → "Other papers". */
export function sessionLabel(series: string | null): string {
  if (!series) return "Other papers";
  return series.charAt(0).toUpperCase() + series.slice(1);
}

function sessionKey(series: string | null): number {
  if (!series) return -1;
  const [season = "", year = "0"] = series.split(" ");
  return Number(year) * 10 + (SEASON_RANK[season] ?? 0);
}

/** Sessions newest first, each with its papers in name order. */
export function bySession(papers: PastPaper[]): { label: string; papers: PastPaper[] }[] {
  const groups = new Map<string | null, PastPaper[]>();
  for (const p of papers) groups.set(p.series, [...(groups.get(p.series) ?? []), p]);
  return [...groups.entries()]
    .sort(([a], [b]) => sessionKey(b) - sessionKey(a))
    .map(([series, ps]) => ({
      label: sessionLabel(series),
      papers: [...ps].sort((x, y) => x.name.localeCompare(y.name)),
    }));
}

const pages = (docs: Doc[]) => docs.reduce((n, d) => n + (d.page_count ?? 0), 0);

export type Chip = { role: PastPaperRole; label: string; docs: Doc[] };

/** "Question paper · 32 pp", "Question paper · 2 parts · 40 pp", "Mark scheme · 15 pp". */
export function chips(paper: PastPaper): Chip[] {
  const order: PastPaperRole[] = ["question_paper", "insert", "mark_scheme"];
  return order.flatMap((role) => {
    const docs = paper.documents.filter((d) => (d.role ?? "question_paper") === role);
    if (docs.length === 0) return [];
    const parts = docs.length > 1 ? ` · ${docs.length} parts` : "";
    return [{ role, label: `${ROLE_WORD[role]}${parts} · ${pages(docs)} pp`, docs }];
  });
}

export const hasScheme = (p: PastPaper) => p.documents.some((d) => d.role === "mark_scheme");
export const hasQuestionPaper = (p: PastPaper) =>
  p.documents.some((d) => (d.role ?? "question_paper") === "question_paper");

/** "Which paper is this for?": the subject's papers that have no mark scheme yet. */
export function papersWithoutScheme(listing: PastPaperListing): PastPaper[] {
  return listing.papers.filter((p) => !hasScheme(p) && hasQuestionPaper(p));
}

/** "Summer 2025 · 4WPH1/1P", as a select option names a paper. */
export const paperOption = (p: PastPaper) =>
  p.series ? `${sessionLabel(p.series)} · ${p.name}` : p.name;

/** Settled and read: a file the learner and the grouping have seen. */
export const described = (d: Doc) => d.status === "uploaded" && Boolean(d.described_at);
